import React, { createContext, useContext, ReactNode, useEffect, useCallback } from "react";
import { BaseServiceInterface, ServiceConfig, ImageUpload, MeasurementUnit, BodyMeasurementResult } from "../types/unified-services";
import { useUnifiedBaseService } from "../hooks/useUnifiedBaseService";
import { useBodyMeasurementState } from "../hooks/useBodyMeasurementState";
import { useAutoTaggingState } from "../hooks/useAutoTaggingState";
import { useVisualSearchState } from "../hooks/useVisualSearchState";
import { useGarmentMeasurementState } from "../hooks/useGarmentMeasurementState";
import { validateAndCorrectStep } from "../helpers/serviceStepMapper";

interface BodyMeasurementState {
  frontImage: ImageUpload;
  sideImage: ImageUpload;
  height: number | null;
  selectedUnit: MeasurementUnit;
  displayValue: string | null;
  measurementData: BodyMeasurementResult | null;
  availableCharts: any | null;
  sizeGuideResult: any | null;
  loadingCharts: boolean;
  loadingSizeGuide: boolean;
  isClosestMatch: string | null;
  cameraOptedOut: boolean;
}

interface BodyMeasurementActions {
  setFrontImage: (image: ImageUpload) => void;
  setSideImage: (image: ImageUpload) => void;
  setHeight: (height: number | null) => void;
  setSelectedUnit: (unit: MeasurementUnit) => void;
  setDisplayValue: (value: string | null) => void;
  setMeasurementData: (data: BodyMeasurementResult | null) => void;
  setAvailableCharts: (charts: any) => void;
  setSizeGuideResult: (result: any) => void;
  setLoadingCharts: (loading: boolean) => void;
  setLoadingSizeGuide: (loading: boolean) => void;
  setIsClosestMatch: (match: string | null) => void;
  setCameraOptedOut: (optedOut: boolean) => void;
  resetBodyMeasurement: () => void;
  getValidationProps: () => { min: number; max: number; step: number };
  convertValue: (value: number) => string;
  handleHeightChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleUnitChange: (unit: MeasurementUnit) => void;
  getImagesForService: () => { frontImage: string | null; sideImage: string | null };
  getOriginalImages: () => { frontImage: string | null; sideImage: string | null };
  hasBlurredImages: () => boolean;
  clearBlurredImages: () => void;
}

interface UnifiedServiceContextType {
  serviceInterface: BaseServiceInterface;
  serviceConfig: ServiceConfig;

  bodyMeasurement: BodyMeasurementState & BodyMeasurementActions;
  autoTagging: ReturnType<typeof useAutoTaggingState>;
  visualSearch: ReturnType<typeof useVisualSearchState>;
  garmentMeasurement: ReturnType<typeof useGarmentMeasurementState>;

  resetAll: () => void;
}

const UnifiedServiceContext = createContext<UnifiedServiceContextType | undefined>(undefined);

interface UnifiedServiceProviderProps {
  children: ReactNode;
  config: ServiceConfig;
}

export const UnifiedServiceProvider: React.FC<UnifiedServiceProviderProps> = ({ children, config }) => {
  const serviceInterface = useUnifiedBaseService();
  const bodyMeasurementState = useBodyMeasurementState();
  const autoTaggingState = useAutoTaggingState();
  const visualSearchState = useVisualSearchState();
  const garmentMeasurementState = useGarmentMeasurementState();

  const resetAllStates = useCallback(() => {
    serviceInterface.resetService();
    bodyMeasurementState.resetBodyMeasurement();
    autoTaggingState.resetAutoTagging();
    visualSearchState.resetVisualSearch();
    garmentMeasurementState.resetGarmentMeasurement();
  }, [serviceInterface, bodyMeasurementState, autoTaggingState, visualSearchState, garmentMeasurementState]);

  useEffect(() => {
    resetAllStates();

    const currentStep = serviceInterface.currentStep;
    const correctedStep = validateAndCorrectStep(currentStep, config);

    if (correctedStep !== currentStep) {
      serviceInterface.setCurrentStep(correctedStep);
    }
  }, [config.serviceId, config]);

  const resetAll = () => {
    resetAllStates();
  };

  const value: UnifiedServiceContextType = {
    serviceInterface,
    serviceConfig: config,
    bodyMeasurement: bodyMeasurementState,
    autoTagging: autoTaggingState,
    visualSearch: visualSearchState,
    garmentMeasurement: garmentMeasurementState,
    resetAll,
  };

  return <UnifiedServiceContext.Provider value={value}>{children}</UnifiedServiceContext.Provider>;
};

export const useUnifiedService = (): UnifiedServiceContextType => {
  const context = useContext(UnifiedServiceContext);
  if (!context) {
    throw new Error("useUnifiedService must be used within a UnifiedServiceProvider");
  }
  return context;
};
